import { Draft, produce } from 'immer';
import React, {
  Reducer,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

export type ErrorValues<T> = {
  [k in keyof T]?: string;
};

export type State<T> = {
  values: T;
  errors: ErrorValues<T>;
};

export type Validator<T> = {
  [key in keyof T]?: (value: T[key], state: State<T>) => string;
};

export type Actions<T> =
  | {
      type: 'error';
      errors: ErrorValues<T>;
    }
  | {
      type: 'update';
      field: keyof T;
      value: any;
      validators: Validator<T>;
    };

const reducer = <T>(state: State<T>, action: Actions<T>) => {
  return produce(state, (s) => {
    switch (action.type) {
      case 'error': {
        s.errors = action.errors as Draft<ErrorValues<T>>;
        break;
      }
      case 'update': {
        const { validators } = action;
        const { value, field } = action;
        // @ts-ignore
        s.values[field] =
          typeof value === 'function' ? value(state.values) : value;
        if (validators[field]) {
          const err = validators[field](value, state);
          if (err) {
            // @ts-ignore
            s.errors[field] = err;
          } else {
            // @ts-ignore
            delete s.errors[field];
          }
        }
        break;
      }
      default:
    }
  });
};

export const useForm = <T>(initialValues: T, validators = {}) => {
  const [form, dispatch] = useReducer<
    Reducer<State<T>, Actions<T>>,
    Actions<T>
  >(reducer, null, () => ({
    values: initialValues,
    errors: {},
  }));

  const methods = useMemo(
    () => ({
      validate(state, keys = Object.keys(state.values)) {
        const errors = {};
        keys.forEach((k) => {
          if (validators[k]) {
            const err = validators[k](state.values[k], state);
            if (err) {
              errors[k] = err;
            }
          }
        });

        if (Object.keys(errors).length > 0) {
          dispatch({ type: 'error', errors });
          return errors;
        }

        return null;
      },
      setValue<Field extends keyof T>(
        field: Field,
        value: T[Field] | ((values: T) => T[Field])
      ) {
        dispatch({ type: 'update', field, value, validators });
      },
      updateErrors(errors: ErrorValues<T>) {
        dispatch({ type: 'error', errors });
      },
      pushIndex<Field extends keyof T>({
        field,
        value,
        index,
      }: {
        field: Field;
        value: T[Field] extends (infer U)[] ? U : never;
        index?: number;
      }) {
        dispatch({
          type: 'update',
          field,
          value: (s) => {
            const arr = [...(s[field] || [])];
            arr.splice(index, 0, value);
            return arr;
          },
          validators,
        });
      },
      moveIndex<Field extends keyof T>({
        field,
        srcIndex,
        dstIndex,
      }: {
        field: Field;
        srcIndex: number;
        dstIndex: number;
      }) {
        dispatch({
          type: 'update',
          field,
          value: (s) => {
            const arr = [...s[field]];
            const [removed] = arr.splice(srcIndex, 1);
            arr.splice(dstIndex, 0, removed);
            return arr;
          },
          validators,
        });
      },
      deleteIndex<Field extends keyof T>({
        field,
        index,
      }: {
        field: Field;
        index: number;
      }) {
        dispatch({
          type: 'update',
          field,
          value: (s) => {
            const arr = [...s[field]];
            arr.splice(index, 1);
            return arr;
          },
          validators,
        });
      },
    }),
    [validators]
  );

  return [form, methods] as [typeof form, typeof methods];
};

export const useDebouncedState = <T>(value: T, delay = 100) => {
  const [debounced, setDebounced] = useState<T>(value);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebounced(value);
    }, delay);
    return () => clearTimeout(timeout);
  }, [setDebounced, delay, value]);

  return debounced;
};
export const useOnScrollEnd = (
  cb: (e: React.UIEvent<HTMLElement, UIEvent>) => void,
  threshold: number = 300
) => {
  const ref = useRef<typeof cb>();
  ref.current = cb;

  return useCallback(
    (e: React.UIEvent<HTMLElement, UIEvent>) => {
      if (!ref.current) {
        return;
      }

      const target = e.target as HTMLElement;
      if (
        target.scrollTop + target.clientHeight >=
        target.scrollHeight - threshold
      ) {
        ref.current(e);
      }
    },
    [threshold]
  );
};
